import { Loader } from "@/components/Loader/Loader.tsx";
import { isInquiryPaymentType } from "@/helpers/isInquiryPaymentType.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.tsx";
import { useRenderCheckReservationOverviewSubmit } from "@/pages/ReservationOverview/components/ReservationOverviewSubmit/hooks/useRenderCheckReservationOverviewSubmit.ts";
import { ReservationOverviewSubmitProps } from "@/pages/ReservationOverview/components/ReservationOverviewSubmit/ReservationOverviewSubmitProps.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { useValidFormStore } from "@/stores/ValidFormStore/ValidFormStore.ts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const ReservationOverviewSubmit = (props: ReservationOverviewSubmitProps) => {
  const { t } = useTranslation();
  const { handleSubmitCart, submitCartLoading, isReserved } = props;
  const { defaultDateRange, cartAccommodationItems, calculatedCartData, setInquiryOverride, acceptedGDPR } =
    useEngineStore(
      useShallow((state) => ({
        defaultDateRange: state.defaultDateRange,
        cartAccommodationItems: state.cartAccommodationItems,
        calculatedCartData: state.calculatedCartData,
        setInquiryOverride: state.setInquiryOverride,
        acceptedGDPR: state.acceptedGDPR,
      })),
    );

  const isForcedInquiry = isInquiryPaymentType(calculatedCartData);
  const isValid = useValidFormStore((state) => state.isValid);
  const { hasAccTypesForPaymentInCart, hasAccTypeCategoriesForPaymentInCart } =
    useRenderCheckReservationOverviewSubmit();
  const { paymentEnabled } = useGetOrganization();

  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [reservationLoading, setReservationLoading] = useState(false);

  // If cart item matched accType/accTypeCategory defined in rootAttributes --> use dual button submit section
  // NOTE: Works only if payment is enabled on PMS
  const canHaveInquiryAndReservation = useMemo(() => {
    if (!isForcedInquiry && paymentEnabled) {
      return hasAccTypesForPaymentInCart || hasAccTypeCategoriesForPaymentInCart;
    }
    return false;
  }, [hasAccTypesForPaymentInCart, hasAccTypeCategoriesForPaymentInCart, isForcedInquiry, paymentEnabled]);

  const isContinueDisabled = useMemo(() => {
    return (
      !cartAccommodationItems?.length ||
      !(defaultDateRange?.[0] && defaultDateRange?.[1]) ||
      inquiryLoading ||
      reservationLoading ||
      !isValid ||
      !acceptedGDPR
    );
  }, [
    acceptedGDPR,
    cartAccommodationItems?.length,
    isValid,
    defaultDateRange,
    inquiryLoading,
    reservationLoading,
  ]);

  const handleInquirySubmit = async () => {
    try {
      setInquiryOverride(true);
      setInquiryLoading(true);
      await handleSubmitCart();
    } finally {
      setInquiryLoading(false);
    }
  };

  // NOTE: Payment must be enabled on PMS for this to work.
  const handleReservationSubmit = async () => {
    try {
      setInquiryOverride(false);
      setReservationLoading(true);
      await handleSubmitCart();
    } finally {
      setReservationLoading(false);
    }
  };

  if (canHaveInquiryAndReservation) {
    return (
      <div className="c-reservation-overview__submit-section">
        <button
          className="c-be-button--secondary"
          disabled={isContinueDisabled || isReserved}
          onClick={handleInquirySubmit}
        >
          <span className="c-be-button__label">
            {submitCartLoading && inquiryLoading ? <Loader small loading /> : t("sendInquiry")}
          </span>
        </button>
        <button
          className="c-be-button"
          disabled={isContinueDisabled || isReserved}
          onClick={handleReservationSubmit}
        >
          <span className="c-be-button__label">
            {submitCartLoading && reservationLoading ? (
              <Loader customFill={"#ffffff"} small loading />
            ) : (
              t("finishReservation")
            )}
          </span>
        </button>
      </div>
    );
  }

  return (
    <button
      className="c-be-button"
      disabled={isContinueDisabled || isReserved || submitCartLoading}
      onClick={() => {
        setInquiryOverride(false);
        void handleSubmitCart();
      }}
    >
      <span className="c-be-button__label">
        {submitCartLoading ? (
          <Loader customFill={"#ffffff"} small loading />
        ) : isForcedInquiry ? (
          t("sendInquiry")
        ) : (
          t("finishReservation")
        )}
      </span>
    </button>
  );
};
